/**
 * SuperAdmin staff-monitoring API client.
 *
 * Two distinct surfaces, deliberately kept apart:
 *  • `sendHeartbeat` — called by EVERY employee about themselves (`/api/staff-presence/heartbeat`).
 *  • everything else — SuperAdmin-only reads (`/api/staff-monitoring/*`). A 403 here is expected
 *    for any other role and is surfaced as an error rather than an empty list, so the UI can say
 *    "not permitted" instead of pretending there is no data.
 */
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import type { PaginatedResponse } from "@/lib/types";

export type StaffPresence = "Online" | "Idle" | "Offline";

export interface HeartbeatResult {
  sessionId: string;
  presence: StaffPresence;
  presenceLabel: string;
  sessionActiveSeconds: number;
  sessionDurationSeconds: number;
  startedNewSession: boolean;
}

export interface StaffMonitoringDashboard {
  totalStaff: number;
  currentlyOnline: number;
  currentlyIdle: number;
  currentlyOffline: number;
  averageDailyActiveMinutes: number;
  averageSessionMinutes: number;
  loginsToday: number;
  logoutsToday: number;
  windowFromUtc: string;
  windowToUtc: string;
  generatedAtUtc: string;
}

export interface StaffListRow {
  userId: string;
  fullName: string | null;
  email: string;
  jobTitle: string | null;
  department: string | null;
  roles: string[];
  presence: StaffPresence;
  presenceLabel: string;
  presenceDetail: string;
  currentPage: string | null;
  lastSeenAt: string | null;
  activeMinutesInWindow: number;
  sessionsInWindow: number;
  lastLoginAt: string | null;
  lastLogoutAt: string | null;
  currentSessionMinutes: number | null;
  /** Replies sent in the window more than 24h after the message they answered. */
  lateRepliesInWindow: number;
  /** Messages sent in the window after 4:30 PM Cairo time. */
  afterHoursMessagesInWindow: number;
  isActive: boolean;
}

export interface StaffSessionRow {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationMinutes: number;
  activeMinutes: number;
  endReasonLabel: string;
  isOpen: boolean;
  browser: string | null;
  operatingSystem: string | null;
  deviceType: string | null;
  ipAddress: string | null;
  currentPage: string | null;
}

export interface StaffEventRow {
  id: string;
  eventTypeLabel: string;
  occurredAt: string;
  page: string | null;
  ipAddress: string | null;
}

export interface StaffPageUsage {
  path: string;
  visits: number;
  totalSeconds: number;
}

export interface StaffHeatCell {
  dateUtc: string;
  hour: number;
  activeSeconds: number;
}

export interface StaffDetail {
  userId: string;
  fullName: string | null;
  email: string;
  jobTitle: string | null;
  department: string | null;
  isActive: boolean;
  roles: string[];
  permissions: string[];
  presence: StaffPresence;
  presenceLabel: string;
  presenceDetail: string;
  lastSeenAt: string | null;
  lastLoginAt: string | null;
  lastLogoutAt: string | null;
  currentSession: StaffSessionRow | null;
  currentPage: string | null;
  currentPageSeconds: number | null;
  activity: {
    todayMinutes: number;
    yesterdayMinutes: number;
    thisWeekMinutes: number;
    thisMonthMinutes: number;
    customRangeMinutes: number | null;
  };
  averageDailyActiveMinutes: number;
  averageWeeklyActiveMinutes: number;
  averageMonthlyActiveMinutes: number;
  averageSessionMinutes: number;
  totalSessions: number;
  daysSinceLastLogin: number;
  mostUsedPages: StaffPageUsage[];
  activeSecondsByHour: number[];
  activeSecondsByDayOfWeek: number[];
  heatMap: StaffHeatCell[];
  mostActiveHour: number;
  mostActiveDayOfWeek: number;
  windowFromUtc: string;
  windowToUtc: string;
}

export interface StaffTimelineEntry {
  atUtc: string;
  kind: "login" | "logout" | "session-start" | "session-end" | "token-expired" | "page";
  label: string;
  page: string | null;
  durationSeconds: number | null;
}

export interface TeamLeaderLeaderboardRow {
  userId: string;
  fullName: string | null;
  email: string;
  department: string | null;
  hoursToday: number;
  hoursThisWeek: number;
  hoursThisMonth: number;
  studentsSupervised: number;
  messagesSent: number;
  examsReviewed: number;
  /** Assignment attempts graded or returned for revision in the window. */
  assignmentsReviewed: number;
  certificatesIssued: number;
  crmActions: number;
  averageResponseMinutes: number | null;
  daysWithoutLogin: number;
  activityScore: number;
  presence: StaffPresence;
  presenceLabel: string;
}

export interface StaffFilterParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  role?: string;
  status?: string;
  department?: string;
  fromUtc?: string;
  toUtc?: string;
  sortBy?: string;
  sortDir?: string;
}

/** Surfaces the server's reason instead of a generic failure — 403 here is meaningful. */
async function readJson<T>(res: Response, what: string): Promise<T> {
  if (res.status === 403) throw new Error(`Only a Super Administrator can view ${what}.`);
  if (!res.ok) {
    const body = (await res.text().catch(() => "")).trim();
    let message = body;
    if (body) {
      try {
        const parsed = JSON.parse(body);
        if (typeof parsed === "string") {
          message = parsed;
        } else if (parsed && typeof parsed === "object") {
          if (typeof parsed.message === "string" && parsed.message.trim()) {
            message = parsed.message.trim();
          } else if (parsed.errors && typeof parsed.errors === "object") {
            const errList = Object.entries(parsed.errors)
              .flatMap(([, v]) => (Array.isArray(v) ? v : [String(v)]))
              .filter(Boolean);
            if (errList.length > 0) {
              message = errList.join(", ");
            } else if (typeof parsed.title === "string" && parsed.title.trim()) {
              message = parsed.title.trim();
            }
          } else if (typeof parsed.title === "string" && parsed.title.trim()) {
            message = parsed.title.trim();
          }
        }
      } catch {
        // Keep raw body if JSON parsing fails
      }
    }
    throw new Error(message || `Failed to load ${what} (${res.status})`);
  }
  return (await res.json()) as T;
}

// ── Self-service heartbeat ───────────────────────────────────────────────────

/**
 * One row-touch per employee per minute. Returns null on any failure — a heartbeat is fire and
 * forget and must never surface an error toast or break the page the employee is working on.
 */
export async function sendHeartbeat(currentPage: string): Promise<HeartbeatResult | null> {
  if (!API_BASE_URL) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/staff-presence/heartbeat`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ currentPage }),
    });
    if (!res.ok || res.status === 204) return null;
    return (await res.json()) as HeartbeatResult;
  } catch {
    return null;
  }
}

// ── SuperAdmin reads ─────────────────────────────────────────────────────────

export async function fetchStaffDashboard(
  params: { fromUtc?: string; toUtc?: string } = {},
): Promise<StaffMonitoringDashboard> {
  const qs = new URLSearchParams();
  if (params.fromUtc) qs.set("fromUtc", params.fromUtc);
  if (params.toUtc) qs.set("toUtc", params.toUtc);
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/dashboard?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "the monitoring dashboard");
}

export async function fetchStaffList(
  params: StaffFilterParams,
): Promise<PaginatedResponse<StaffListRow>> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== "all") qs.set(k, String(v));
  });
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/staff?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "the staff list");
}

export async function fetchStaffDepartments(): Promise<string[]> {
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/departments`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "departments");
}

export async function fetchStaffDetail(
  userId: string,
  params: { fromUtc?: string; toUtc?: string } = {},
): Promise<StaffDetail> {
  const qs = new URLSearchParams();
  if (params.fromUtc) qs.set("fromUtc", params.fromUtc);
  if (params.toUtc) qs.set("toUtc", params.toUtc);
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/staff/${userId}?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "this employee's activity");
}

export async function fetchStaffSessions(
  userId: string,
  params: { pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedResponse<StaffSessionRow>> {
  const qs = new URLSearchParams();
  qs.set("pageNumber", String(params.pageNumber ?? 1));
  qs.set("pageSize", String(params.pageSize ?? 25));
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/staff/${userId}/sessions?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "sessions");
}

export async function fetchStaffEvents(
  userId: string,
  params: { pageNumber?: number; pageSize?: number; eventType?: string } = {},
): Promise<PaginatedResponse<StaffEventRow>> {
  const qs = new URLSearchParams();
  qs.set("pageNumber", String(params.pageNumber ?? 1));
  qs.set("pageSize", String(params.pageSize ?? 50));
  if (params.eventType) qs.set("eventType", params.eventType);
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/staff/${userId}/events?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "the event log");
}

export async function fetchStaffTimeline(
  userId: string,
  dayUtc?: string,
): Promise<StaffTimelineEntry[]> {
  const qs = new URLSearchParams();
  if (dayUtc) qs.set("dayUtc", dayUtc);
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/staff/${userId}/timeline?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "the activity timeline");
}

export async function fetchTeamLeaderLeaderboard(
  params: { fromUtc?: string; toUtc?: string } = {},
): Promise<{
  rows: TeamLeaderLeaderboardRow[];
  windowFromUtc: string;
  windowToUtc: string;
  generatedAtUtc: string;
}> {
  const qs = new URLSearchParams();
  if (params.fromUtc) qs.set("fromUtc", params.fromUtc);
  if (params.toUtc) qs.set("toUtc", params.toUtc);
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/leaderboard/team-leaders?${qs}`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "the leaderboard");
}

export interface LeaderboardWeights {
  hours: number;
  studentsSupervised: number;
  messagesSent: number;
  examsReviewed: number;
  assignmentsReviewed: number;
  certificatesIssued: number;
  crmActions: number;
  responsiveness: number;
}

export async function fetchLeaderboardWeights(): Promise<LeaderboardWeights> {
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/leaderboard/weights`, {
    headers: getAuthHeaders(),
  });
  return readJson(res, "leaderboard weights");
}

export async function updateLeaderboardWeights(w: LeaderboardWeights): Promise<LeaderboardWeights> {
  const res = await fetch(`${API_BASE_URL}/staff-monitoring/leaderboard/weights`, {
    method: "PUT",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(w),
  });
  return readJson(res, "leaderboard weights");
}

/**
 * Triggers a browser download of a monitoring report. Reads the filename from the response rather
 * than reconstructing it, so the server stays the single source of truth for report naming.
 */
export async function downloadStaffReport(
  kind: "sessions" | "activetime" | "connection" | "usage",
  format: "csv" | "excel" | "pdf",
  params: { fromUtc?: string; toUtc?: string } = {},
): Promise<void> {
  const qs = new URLSearchParams({ format });
  if (params.fromUtc) qs.set("fromUtc", params.fromUtc);
  if (params.toUtc) qs.set("toUtc", params.toUtc);

  const res = await fetch(`${API_BASE_URL}/staff-monitoring/reports/${kind}?${qs}`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 403)
    throw new Error("Only a Super Administrator can export monitoring reports.");
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || "Export failed");

  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match
    ? decodeURIComponent(match[1])
    : `staff-${kind}.${format === "excel" ? "xlsx" : format}`;

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
